import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';

import {
  LongFormInfoInput,
  LongFormPrequalResponse,
} from '../shared/models/prequal.types';
import { getAdobeECID } from '../shared/utils/adobe-ecid';
import { uuid } from '../shared/utils/uuid';
import {
  ReSubmitLongFormApplicationResponse,
  SubmitLongFormApplicationResponse,
} from './prequal-rest-responses-interface';
import { RuntimeConfigService } from './runtime-config';

/**
 * HTTP client for submitting and re-submitting the completed long-form
 * application to WWW REST (`/api/longform/submit` and `/resubmit`). Attaches
 * correlation id, Adobe ECID, and either a reCAPTCHA token or test-script header.
 */
@Injectable({
  providedIn: 'root',
})
export class LongFormApplicationRestService {
  constructor(
    private http: HttpClient,
    private runtimeConfigService: RuntimeConfigService
  ) {}

  submitLongFormApplication(
    longFormInfoInput: LongFormInfoInput,
    cheaders:
      | { 'Is-Test-Script': string }
      | {
          recaptcha: string;
        }
  ): Observable<SubmitLongFormApplicationResponse> {
    const { WWW_REST_API_URI } = this.runtimeConfigService.prequalConfig;
    return getAdobeECID().pipe(
      take(1),
      switchMap((ecid) => {
        const headers = {
          'x-correlation-id': uuid(),
          'x-ecid': ecid || '',
          ...cheaders,
        };

        return this.http.post<SubmitLongFormApplicationResponse>(
          WWW_REST_API_URI + '/api/longform/submit',
          longFormInfoInput,
          { headers }
        );
      })
    );
  }

  reSubmitLongFormApplication(
    longFormInfoInput: LongFormInfoInput,
    cheaders:
      | { 'Is-Test-Script': string }
      | {
          recaptcha: string;
        }
  ): Observable<ReSubmitLongFormApplicationResponse> {
    const { WWW_REST_API_URI } = this.runtimeConfigService.prequalConfig;

    return getAdobeECID().pipe(
      take(1),
      switchMap((ecid) => {
        const headers = {
          'x-correlation-id': uuid(),
          'x-ecid': ecid || '',
          ...cheaders,
        };

        return this.http.post<ReSubmitLongFormApplicationResponse>(
          WWW_REST_API_URI + '/api/longform/resubmit',
          longFormInfoInput,
          { headers }
        );
      })
    );
  }
}
